import { localAddressSearch } from '../services/localAddressSearch';

export interface AddressSuggestion {
  display_name: string;
  lat: string;
  lon: string;
  place_id: number;
  type: string;
  importance: number;
}

export interface AddressSearchResult {
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  placeId: number;
}

class AddressSearchService {
  private readonly DEBOUNCE_DELAY = 300; // milliseconds
  private debounceTimer: NodeJS.Timeout | null = null;
  async searchAddresses(query: string, limit: number = 5): Promise<AddressSuggestion[]> {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const queryTrimmed = query.trim();
    
    // Always search local address dataset (fast, offline-capable, based on GeoJSON).
    // Local search works with just 2 characters.
    const localResults = await localAddressSearch.search(queryTrimmed, limit);
    
    // Only use local results; never fall back to Nominatim.
    return localResults.slice(0, limit);
  }

  /**
   * Debounced search function to avoid too many API calls
   * Now uses hybrid search (local + Nominatim)
   */
  debouncedSearch(
    query: string, 
    callback: (results: AddressSuggestion[]) => void,
    limit: number = 5
  ): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(async () => {
      const results = await this.searchAddresses(query, limit);
      callback(results);
    }, this.DEBOUNCE_DELAY);
  }

  /**
   * Format address suggestion for display
   */
  formatAddressSuggestion(suggestion: AddressSuggestion): string {
    // Extract the most relevant parts of the address
    const parts = suggestion.display_name.split(', ');
    
    // For local addresses (from our dataset), format differently
    // Local addresses have format: "number addition street, postcode Amsterdam, Nederland"
    if (parts.length >= 2 && parts[1].match(/^\d{4}\s?[A-Z]{2}/)) {
      // This looks like a local address (postcode format)
      const streetPart = parts[0].trim();
      const postcode = parts[1].trim();
      return `${streetPart}, ${postcode}`;
    }
    
    // For Amsterdam addresses from Nominatim, we typically want:
    // Street + Number, Amsterdam (or district)
    if (parts.length >= 2) {
      const streetPart = parts[0];
      
      // Look for Amsterdam or district names
      const amsterdamParts = parts.filter(part => 
        part.toLowerCase().includes('amsterdam') || 
        part.toLowerCase().includes('centrum') ||
        part.toLowerCase().includes('zuid') ||
        part.toLowerCase().includes('noord') ||
        part.toLowerCase().includes('oost') ||
        part.toLowerCase().includes('west') ||
        part.toLowerCase().includes('nieuw-west') ||
        part.toLowerCase().includes('zuidoost')
      );
      
      if (amsterdamParts.length > 0) {
        return `${streetPart}, ${amsterdamParts[0]}`;
      }
      
      // Fallback to first two parts
      return `${streetPart}, ${parts[1]}`;
    }
    
    return suggestion.display_name;
  }

  /**
   * Convert suggestion to our AddressSearchResult format
   */
  suggestionToResult(suggestion: AddressSuggestion): AddressSearchResult {
    return {
      address: this.formatAddressSuggestion(suggestion),
      coordinates: {
        lat: parseFloat(suggestion.lat),
        lng: parseFloat(suggestion.lon)
      },
      placeId: suggestion.place_id
    };
  }
}

export const addressSearchService = new AddressSearchService();
